import { useEffect, useRef } from 'react'
import { createRootRouteWithContext, Outlet, useNavigate } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'sonner'
import { registerServiceWorker } from '@/lib/push/register-sw'
import { SoftAskBanner } from '@/components/push/soft-ask-banner'
import type { QueryClient } from '@tanstack/react-query'
import type { useAuth0 } from '@auth0/auth0-react'

interface RouterContext {
  auth: ReturnType<typeof useAuth0>
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
})

function RootComponent() {
  const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    registerServiceWorker().then((registration) => {
      swRegistrationRef.current = registration
    })

    function handleSwMessage(event: MessageEvent) {
      if (event.data?.type !== 'PUSH_RECEIVED') return
      const { title, body, url } = event.data.payload ?? {}
      toast(title || 'Agents Hub', {
        description: body,
        action: url
          ? { label: 'View', onClick: () => navigate({ to: url }) }
          : undefined,
      })
    }

    navigator.serviceWorker?.addEventListener('message', handleSwMessage)
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage)
    }
  }, [navigate])

  return (
    <>
      <Outlet />
      <SoftAskBanner />
      <Toaster position="top-right" richColors />
      {import.meta.env.DEV && !import.meta.env.VITE_E2E && (
        <>
          <TanStackRouterDevtools position="bottom-right" />
          <ReactQueryDevtools buttonPosition="bottom-left" />
        </>
      )}
    </>
  )
}
